import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Injector,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import {
    IChecklist2ReportAnswer,
    IChecklist2ReportChecklistSummary,
    IChecklist2ReportChecklistUnits,
    IChecklist2ReportCheckpoints,
} from '../../../shared/fi-checklist2-report';
import { BehaviorSubject, Subject } from 'rxjs';
import { ChecklistProgressType, FiChecklist2ReportType } from '../../../enums/fi-checklist2-report-type';
import { Animations } from '@fi-shared/utils/animations';
import { FiChecklist2ReportDetailsViewerBase } from '../../../shared/fi-checklist2-report-details-viewer-base';
import { flattenSimple } from '@fi-shared/utils/flatten-simple';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { ChecklistV2ReportPages } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-pages.enum';
import { FiChecklistReportAvailableBusinessUnitsChecker } from '@fi-shared/checklist/fi-checklist2-report/components/shared/available-business-uints-checker';

@Component({
    selector: 'fi-open-checklist-details',
    templateUrl: './open-checklist-details.component.html',
    styleUrls: ['./open-checklist-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-open-checklist-details' },
    animations: [Animations.fade],
    standalone: false,
})
export class FiOpenChecklistDetailsComponent extends FiChecklist2ReportDetailsViewerBase implements OnInit {
    filteredChecklistUnits$ = new BehaviorSubject<IChecklist2ReportChecklistUnits[]>([]); // for chart
    filteredChecklistUnitsForList$ = new BehaviorSubject<IChecklist2ReportChecklistUnits[]>([]); // for list
    filteredCheckpoints$ = new BehaviorSubject<IChecklist2ReportCheckpoints[]>([]);
    isUnitsInitialLoadFinished$ = new BehaviorSubject<boolean>(false);

    isAttachmentListVisiblePage$ = new BehaviorSubject<boolean>(false);
    private progressTypeFilter: ChecklistProgressType;
    protected availableBusinessUnitsChecker: FiChecklistReportAvailableBusinessUnitsChecker;
    protected answersChanged$ = new Subject<void>(); // for reloading gallery (FiChecklistAttachmentsGalleryCard)

    private _availableScheduleIds$ = new BehaviorSubject<number[]>(undefined);
    protected get availableScheduleIds$() {
        return this._availableScheduleIds$;
    }

    constructor(
        injector: Injector,
        private cdr: ChangeDetectorRef,
    ) {
        super(injector);
    }

    ngOnInit() {
        super.ngOnInit();
        this.setInitialReportDetailsData();
        this.setAvailableScheduleIds();

        // for smart updating available business units on filter change
        this.availableBusinessUnitsChecker = new FiChecklistReportAvailableBusinessUnitsChecker(
            this.injector,
            FiChecklist2ReportType.OpenChecklists,
            this.reportData.auditMode,
        );

        this.loadChecklistUnitsAndCheckpoints().finally(() => this.isUnitsInitialLoadFinished$.next(true));

        this.fiChecklist2ReportDataService.subscribeOnFilterOrDateRangeChanged(
            this.reportParameter.Id,
            this.ngOnDestroy$,
            () => {
                this.loadChecklistUnitsAndCheckpoints();
                this.answersChanged$.next();
            },
        );
    }

    private setAvailableScheduleIds() {
        const scheduleIds = this.openChecklistsSummary
            .filter((summary) => summary.ChecklistId === this.currentFilter.ChecklistIds[0])
            .map((summary) => summary.ScheduleId);
        this._availableScheduleIds$.next(scheduleIds);
    }

    private loadChecklistUnitsAndCheckpoints(): Promise<void> {
        const promise = new Promise<void>((resolve, reject) => {
            this.fiChecklist2ReportOpenChecklistsService
                .getChecklistUnits(this.currentFilter, this.reportData.auditMode)
                .then((checklistUnits) => {
                    this.filteredChecklistUnits$.next(checklistUnits);
                    this.filterChecklistUnitsForList();

                    this.availableBusinessUnitsChecker.setAvailableBusinessUnitIds(
                        checklistUnits.map((unit) => unit.BusinessUnitId),
                        this.currentFilter,
                    );

                    this.fiChecklist2ReportOpenChecklistsService
                        .getCheckpoints(this.currentFilter, this.reportData.auditMode)
                        .then((checkpoints) => {
                            this.filteredCheckpoints$.next(checkpoints);
                            resolve();
                        }, resolve);
                }, reject);
        });
        return promise;
    }

    private setInitialReportDetailsData() {
        // commented out
        // https://chainformation.atlassian.net/browse/FI-6215?focusedCommentId=58525
        /*if (this.doesFilterIncludeBusinessUnitIds(this.currentFilter) && (
            this.doesFilterIncludeAreaIds(this.currentFilter) || this.doesFilterIncludeBusinessAreaIds(this.currentFilter))) {
            this.currentFilter.AreaIds = [];
            this.currentFilter.BusinessAreaIds = [];
        }*/
    }

    setNewChecklistSummaryBySchedule() {
        this.checklistSummary = this.openChecklistsSummary.find((summary: IChecklist2ReportChecklistSummary) => {
            return (
                summary.ChecklistId === this.currentFilter.ChecklistIds[0] &&
                summary.ScheduleId === this.currentFilter.ScheduleIds[0]
            );
        });
    }

    setCurrentListPage(isAttachmentsList: boolean) {
        this.isAttachmentListVisiblePage$.next(isAttachmentsList);
    }

    back() {
        if (!!this.isAttachmentListVisiblePage$.value) {
            this.isAttachmentListVisiblePage$.next(false);
        } else {
            this.showWidgetDetails.emit();
        }
    }

    filterUnitsByChart(checklistProgressType: ChecklistProgressType) {
        this.progressTypeFilter = checklistProgressType;
        this.filterChecklistUnitsForList();
        this.cdr.detectChanges();
    }

    getNokOkCheckpoints(): IChecklist2ReportAnswer[] {
        const allAnswerDetails: IChecklist2ReportAnswer[] = flattenSimple(
            this.filteredChecklistUnits$.value.map((checklist) => {
                return checklist.AnswerDetails;
            }),
        );
        return this.fiChecklist2ReportHelperService.getNotOkCheckpoints(allAnswerDetails);
    }

    /*filterReportData() {
        const filteredChecklistUnits = this.fiChecklist2ReportFilterService.getFilteredChecklistUnits(this.openChecklistsData.ChecklistUnits,
            this.currentFilter,
            undefined,
            this.availableItems);

        this.filteredChecklistUnits$.next(filteredChecklistUnits);

        // also we should filter checklist units by progress type (from chart)
        this.filterChecklistUnitsForList();

        const filteredCheckpoints = this.fiChecklist2ReportFilterService.getFilteredCheckpoints(this.openChecklistsData.Checkpoints,
            this.currentFilter,
            undefined,
            this.availableItems)

        this.filteredCheckpoints$.next(filteredCheckpoints);
    }*/

    private filterChecklistUnitsForList() {
        const filteredChecklistUnitsForList = this.fiChecklist2ReportFilterService.filterChecklistDataByProgressType(
            this.filteredChecklistUnits$.value,
            this.progressTypeFilter,
        );
        this.filteredChecklistUnitsForList$.next(filteredChecklistUnitsForList);
    }

    protected readonly FiChecklist2ReportType = FiChecklist2ReportType;
    protected readonly FiInfoIconMode = FiInfoIconMode;
    protected readonly ChecklistV2ReportPages = ChecklistV2ReportPages;
}
